import React, { useCallback, useMemo, useState } from 'react';
import { useHasEnterpriseFeatures } from '../../hooks/subscriptionHooks';
import { Bot } from '../../models/Bots';
import { UserApp } from '../../models/User';
import { useFlag, useUserSafe } from '../../scripts/hooks';
import { UpsertBotParams } from '../../scripts/hooks/bots';
import { PaywallModal } from '../admin/EnterprisePaywall/EnterprisePaywall';
import { Modal } from '../controls/ui/Modal/Modal';
import { UIButton } from '../controls/ui/UIButton/UIButton';
import { UIIcon } from '../controls/ui/UIIcon/UIIcon';
import { UITextArea } from '../controls/ui/UITextArea/UITextArea';
import { UITextbox } from '../controls/ui/UITextbox/UITextbox';
import { UITooltip } from '../controls/ui/UIToolTip/UIToolTip';
import { AppConnectionsSelect } from './AppConnectionsSelect';
import { EmojiPicker } from './emojiPicker';

interface IBotsModalProps {
  type: 'create' | 'edit';
  bot?: Bot;
  orgWideEnabledApps: UserApp[];
  isInSlackChannel?: boolean;
  closeModal: () => void;
  createBot: (bot: UpsertBotParams) => Promise<void>;
  updateBot: (params: UpsertBotParams) => Promise<void>;
  deleteBot: (id: string) => Promise<void>;
}

// eslint-disable-next-line max-lines-per-function
export const BotsModal: React.FC<IBotsModalProps> = ({
  type,
  bot,
  orgWideEnabledApps,
  isInSlackChannel,
  closeModal,
  createBot,
  updateBot,
  deleteBot,
}) => {
  const user = useUserSafe();
  const [botName, setBotName] = useState(bot?.bot_name ?? '');
  const [description, setDescription] = useState(bot?.description ?? '');
  const [customInstructions, setCustomInstructions] = useState(
    bot?.custom_instructions ?? ''
  );

  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [icon, setIcon] = useState(bot?.icon ?? '🤖');
  const [selectedApps, setSelectedApps] = useState(bot?.apps ?? []);
  const [paywallModalOpen, setPaywallModalOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [showErrors, setShowErrors] = useState(false);

  const botsCustomInstructionsEnabled = useFlag(
    'botsCustomInstructionsEnabled'
  );

  const botAppConnectionsEnabled = useFlag('botsAppConnections');

  const hasEnterpriseFeatures = useHasEnterpriseFeatures();

  const hasEditPermissions = useMemo(
    () => user.admin || bot?.user_id === user.userId || type === 'create',
    [bot?.user_id, user.admin, user.userId, type]
  );

  const handleSubmit = useCallback(async () => {
    setLoading(true);
    if (!botName) {
      setLoading(false);

      setShowErrors(true);
      return;
    }

    const body: UpsertBotParams = {
      id: bot?.id,
      icon,
      bot_name: botName,
      description,
      custom_instructions: customInstructions,
      bot_app_connections: selectedApps.map((app) => app.id),
      enabled_apps: orgWideEnabledApps,
    };

    await (type === 'create'
      ? createBot({
          ...body,
        })
      : updateBot({
          ...body,
        }));

    setLoading(false);

    closeModal();
  }, [
    bot,
    botName,
    closeModal,
    customInstructions,
    description,
    icon,
    orgWideEnabledApps,
    selectedApps,
    createBot,
    updateBot,
    type,
  ]);

  const headerCopy = useMemo(() => {
    return type === 'edit' ? 'Edit Bot' : 'Create Bot';
  }, [type]);

  return (
    <Modal onClose={closeModal}>
      <div className="flex flex-col gap-6">
        <div className="flex justify-between gap-2 items-center">
          <div className="flex gap-2 items-center">
            <UIIcon name="bot" size={32} />
            <h2 className="text-2xl m-0 font-bold">{headerCopy}</h2>
          </div>
          {hasEditPermissions && type === 'edit' && (
            <UITooltip
              title={
                isInSlackChannel
                  ? 'The bot is currently being used in 1 or more Slack Channels.'
                  : 'Delete Bot'
              }
            >
              <div
                className={`flex ${
                  isInSlackChannel
                    ? 'opacity-50 cursor-not-allowed'
                    : 'cursor-pointer'
                }`}
                onClick={() => {
                  if (isInSlackChannel) {
                    return;
                  }

                  setShowDeleteModal(true);
                }}
              >
                <UIIcon name="trash-can" size={20} />
              </div>
            </UITooltip>
          )}
        </div>

        <div className="flex gap-6">
          <div>
            <div className="font-bold">Icon</div>
            <EmojiPicker
              disabled={!hasEditPermissions}
              emoji={icon}
              onSelect={setIcon}
            />
          </div>
          <div>
            <div className="font-bold">Name</div>
            <UITextbox
              error={
                showErrors && !botName ? 'Bot name is required' : undefined
              }
              onChange={setBotName}
              placeholder="Enter Name"
              readonly={!hasEditPermissions}
              value={botName}
            />
          </div>
        </div>

        <div>
          <div className="font-bold">Description</div>
          <UITextbox
            maxLength={256}
            onChange={setDescription}
            placeholder="Use this bot for XYZ"
            readonly={!hasEditPermissions}
            value={description}
          />
        </div>

        {botAppConnectionsEnabled && (
          <div>
            <div className="font-bold">App Connections</div>
            {hasEditPermissions ? (
              <AppConnectionsSelect
                apps={orgWideEnabledApps}
                selectedApps={selectedApps}
                setSelectedApps={setSelectedApps}
              />
            ) : (
              <div className="mt-2 flex gap-2 overflow-auto">
                {selectedApps.map((app) => (
                  <div className="flex items-center gap-2" key={app.id}>
                    <UIIcon name={app.appName} size={20} type="apps" />
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {botsCustomInstructionsEnabled && (
          <div>
            {hasEditPermissions ? (
              hasEnterpriseFeatures ? (
                <div className="font-bold mb-2">Instructions</div>
              ) : (
                <div className="flex justify-between items-center mb-2">
                  <div className="items-center flex gap-2">
                    <div className="flex p-1 bg-sunrise-10 rounded-full">
                      <UIIcon
                        className="text-white"
                        name="star"
                        size={12}
                        type="ui"
                      />
                    </div>
                    <div className="font-bold">Instructions</div>
                  </div>
                  <UIButton
                    onClick={() => {
                      setPaywallModalOpen(true);
                    }}
                    type="secondary"
                  >
                    <div className="flex gap-1 items-center">
                      <UIIcon name="lock" />
                      Upgrade Plan
                    </div>
                  </UIButton>
                </div>
              )
            ) : (
              <div className="font-bold mb-2">Instructions</div>
            )}
            <UITextArea
              disabled={!hasEditPermissions || !hasEnterpriseFeatures}
              minRows={4}
              multilineExpandable
              onChange={setCustomInstructions}
              placeholder={`For questions about support tickets, prioritize answers from Intercom help center and tickets.

Respond with short answers with no more than 200 words.

If you don't know the answer, ask the user to create a ticket in Linear.`}
              value={
                hasEditPermissions && hasEnterpriseFeatures
                  ? customInstructions
                  : ''
              }
            />
            <div className="mt-1 text-smoke-10 text-xs">
              Tip: Learn best practices for adding Instructions{' '}
              <a
                href="https://support.dashworks.ai/slackbot/slackbot-in-channels/instructions"
                rel="noreferrer"
                target="_blank"
              >
                here
              </a>
              .
            </div>
          </div>
        )}
      </div>
      <footer className="flex justify-between mt-6">
        <UIButton onClick={closeModal} type="secondary">
          Cancel
        </UIButton>
        <UIButton onClick={handleSubmit} processing={loading}>
          Save
        </UIButton>
      </footer>

      {paywallModalOpen && (
        <PaywallModal
          closeModal={() => {
            setPaywallModalOpen(false);
          }}
        />
      )}
      {showDeleteModal && (
        <DeleteBotModal
          bot={bot!}
          closeModal={() => {
            setShowDeleteModal(false);
          }}
          closeParentModal={closeModal}
          deleteBot={deleteBot}
        />
      )}
    </Modal>
  );
};

const DeleteBotModal: React.FC<{
  bot: Bot;
  closeModal: () => void;
  closeParentModal: () => void;
  deleteBot: (id: string) => Promise<void>;
}> = ({ bot, closeModal, closeParentModal, deleteBot }) => {
  const [loading, setLoading] = useState(false);
  const handleDelete = async () => {
    setLoading(true);
    await deleteBot(bot.id);
    closeParentModal();
    setLoading(false);
  };

  return (
    <Modal modalClasses="!w-[480px]" onClose={closeModal}>
      <div className="text-lg font-semibold">Delete this Bot</div>
      <div className="mt-2">You and the team will lose access to this bot.</div>
      <div className="flex mt-4 gap-4 justify-between">
        <UIButton onClick={closeModal} type="secondary">
          Cancel
        </UIButton>
        <UIButton onClick={handleDelete} processing={loading} type="delete">
          Delete
        </UIButton>
      </div>
    </Modal>
  );
};
